<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import type {
    PrintTemplateCreateModel,
    PrintTemplateModel,
    PrintTemplateUpdateModel
} from '@/types/models/PrintTemplateModel'
import TemplateFormDialog from '@/views/TemplatesView/components/TemplateFormDialog.vue'
import {
    ClickableTypography,
    CreateButton,
    FullScreenLoader,
    FullTableLayout,
    IconButton,
    MobileShowFiltersButton,
    PrimaryButton,
    StackLayout,
    TagItem
} from '@rfc/rfc-components-library'
import { useTableModel } from '@rfc/rfc-components-library/hooks'
import type { FullTableLayoutActionProps } from '@rfc/rfc-components-library/types'
import { getQueryFilters } from '@rfc/rfc-components-library/utils'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ETemplateActions } from '../../store/templateStore/TemplateStoreTypes'
import { ETemplate, ETemplateColors } from '../../types/enum/TemplateEnum'

const { t } = useI18n()
const store = useStore()
const router = useRouter()

type Props = {
    actionProps?: FullTableLayoutActionProps<PrintTemplateModel>
    baseUrl?: string
}
defineProps<Props>()

const dialogOpen = ref(false)
const selectedTemplate = ref<PrintTemplateModel>()
const templateLoading = computed(() => store.state.printTemplate.loading)

const templatesModel = useTableModel<PrintTemplateModel, { organization_id?: number }>({
    store,
    stateKey: EStoreModules.PRINT_TEMPLATE,
    stateAction: ETemplateActions.FETCH_TEMPLATES,
    router,
    dataFilters: {
        ...getQueryFilters()
    }
})
const columns = [
    { title: t('templates.columns.name'), value: 'name' },
    { title: t('templates.columns.type'), value: 'type' },
    { title: t('templates.columns.version'), value: 'version' },
    { title: t('templates.columns.fileName'), value: 'file_name' },
    { title: t('templates.columns.lastEditor'), value: 'last_editor' }
]

const openCreate = () => {
    selectedTemplate.value = undefined
    dialogOpen.value = true
}

const handleSubmit = async (data: PrintTemplateUpdateModel | PrintTemplateCreateModel) => {
    if (selectedTemplate.value && (data as PrintTemplateUpdateModel).id)
        store
            .dispatch(`${EStoreModules.PRINT_TEMPLATE}/${ETemplateActions.UPDATE_TEMPLATE}`, {
                data,
                id: (data as PrintTemplateUpdateModel).id
            })
            .then(() => templatesModel.getData())
    else {
        store
            .dispatch(`${EStoreModules.PRINT_TEMPLATE}/${ETemplateActions.CREATE_TEMPLATE}`, data)
            .then(() => templatesModel.getData())
    }
    dialogOpen.value = false
}

const handleDownload = async (template: PrintTemplateModel) =>
    store.dispatch(`${EStoreModules.PRINT_TEMPLATE}/${ETemplateActions.DOWNLOAD_TEMPLATE}`, {
        template
    })

const setActionsProps: FullTableLayoutActionProps<PrintTemplateModel> = {
    deletePermission: {
        hasPermission: true
    }
}
</script>

<template>
    <FullScreenLoader :loading="templateLoading" />
    <StackLayout direction="column" :gap="16" class="w-full h-full pa-4">
        <FullTableLayout
            :columns="columns"
            :data="{ ...templatesModel.data.value, ...templatesModel.data.value?.meta }"
            :loading="templatesModel.loading.value"
            :sortBy="templatesModel.sort.value"
            :actionProps="{ ...setActionsProps, ...(actionProps ?? {}) }"
            @change:pageNumber="templatesModel.handleChangePageNumber"
            @change:pageSize="templatesModel.handleChangePageSize"
            @changeSortBy="templatesModel.handleChangeSortBy"
            @removeTag="templatesModel.handleRemoveTag"
            @search="templatesModel.handleSearch"
            translationPath="event.filters"
            class="h-auto">
            <template #filters>
                <MobileShowFiltersButton>
                    <slot
                        name="filters"
                        :filters="templatesModel.filters.value"
                        @change="templatesModel.handleChangeFilters" />
                </MobileShowFiltersButton>
            </template>

            <template #button>
                <CreateButton :label="t('templates.create')" @click="openCreate" />
            </template>

            <template #type="item">
                <TagItem
                    :item="{ label: $t(`templates.types.${item.type}`) }"
                    :color="ETemplateColors[item.type as ETemplate]"
                    :closable="false"
                    size="small" />
            </template>

            <template #last_editor="item">
                <ClickableTypography
                    v-if="item.last_editor"
                    :label="item.last_editor.name"
                    :href="`/admin/users/${item.last_editor.id}`" />
                <span v-else>—</span>
            </template>

            <template #actions="item">
                <IconButton
                    icon="mdi mdi-download"
                    @click="handleDownload(item)"
                    :loading="templateLoading" />
            </template>
        </FullTableLayout>

        <TemplateFormDialog
            :open="dialogOpen"
            :template="selectedTemplate"
            :routeToGo="baseUrl"
            @close="dialogOpen = false"
            @submit="handleSubmit">
            <template #create="props">
                <slot name="create" :data="props.data" @change="props.onChange" />
            </template>
        </TemplateFormDialog>
    </StackLayout>
</template>

<style scoped lang="scss">
.templates-table {
    border-radius: 8px;
    overflow: hidden;
}
</style>
